import { useCallback, useRef, useState } from 'react';
import { defaultPeriodFilter, type PeriodFilter } from '../components/PeriodSelector';

/**
 * O PERÍODO DAS TELAS FINANCEIRAS NA URL (15/10/2026, estado na URL, PR 3: Faturas, Boletos, Despesas, Recebíveis).
 *
 *  - o mês atual (o padrão das quatro): sem parâmetro, a URL fica limpa;
 *  - um ou mais meses: `?mes=2026-09` / `?mes=2026-09,2026-10`;
 *  - "Todos os meses" (months vazio): `?mes=todos`;
 *  - intervalo de datas: `?de=2026-10-01&ate=2026-10-15`.
 *
 * Trocar o período usa REPLACE: o Voltar não anda mês a mês, sai da tela (o histórico é de telas e de cascas). Os outros
 * parâmetros e o `history.state` (as marcas das cascas, o estado do roteador) são preservados. Parâmetro inválido é
 * ignorado e vale o padrão da tela: a URL nunca abre um período que a tela não saberia mostrar.
 */
const MES = /^\d{4}-(0[1-9]|1[0-2])$/;
const DATA = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const CHAVES = ['mes', 'de', 'ate'];

export function periodoDaUrl(search: string): PeriodFilter | null {
  const q = new URLSearchParams(search);
  const de = q.get('de');
  const ate = q.get('ate');
  if (de && ate && DATA.test(de) && DATA.test(ate) && de <= ate) {
    return { mode: 'dateRange', months: [], startDate: de, endDate: ate };
  }
  const mes = q.get('mes');
  if (mes === 'todos') return { mode: 'month', months: [], startDate: '', endDate: '' };
  if (mes) {
    const meses = Array.from(new Set(mes.split(',').map((m) => m.trim())));
    if (meses.length > 0 && meses.every((m) => MES.test(m))) return { mode: 'month', months: meses.sort(), startDate: '', endDate: '' };
  }
  return null;
}

/** A query de um período, preservando os outros parâmetros. O período PADRÃO não escreve nada. */
export function buscaDoPeriodo(search: string, p: PeriodFilter, padrao: PeriodFilter): string {
  const q = new URLSearchParams(search);
  CHAVES.forEach((c) => q.delete(c));
  const igualAoPadrao = p.mode === padrao.mode && p.startDate === padrao.startDate && p.endDate === padrao.endDate
    && [...p.months].sort().join(',') === [...padrao.months].sort().join(',');
  if (!igualAoPadrao) {
    if (p.mode === 'dateRange' && p.startDate && p.endDate) { q.set('de', p.startDate); q.set('ate', p.endDate); }
    else if (p.mode === 'month') q.set('mes', p.months.length === 0 ? 'todos' : [...p.months].sort().join(','));
  }
  const r = q.toString();
  return r ? `?${r}` : '';
}

/**
 * O `useState` do período das telas financeiras, com o período na URL. Lê a URL na montagem; escreve com replace.
 * Aceita o valor ou a função `prev => novo`, como o setter do `useState`. `inicial` é o valor sem parâmetro na URL (em
 * Faturas, com busca, "todos os meses"); o que fica FORA da URL é sempre o mês atual, para o F5 não perder o resto.
 */
export function usePeriodoNaUrl(inicial: PeriodFilter = defaultPeriodFilter()): [PeriodFilter, (p: PeriodFilter | ((prev: PeriodFilter) => PeriodFilter)) => void] {
  const [periodo, setPeriodo] = useState<PeriodFilter>(() => periodoDaUrl(window.location.search) ?? inicial);
  const atual = useRef(periodo);
  atual.current = periodo;
  const padraoRef = useRef(defaultPeriodFilter());
  const definir = useCallback((p: PeriodFilter | ((prev: PeriodFilter) => PeriodFilter)) => {
    const novo = typeof p === 'function' ? p(atual.current) : p;
    atual.current = novo;
    setPeriodo(novo);
    const busca = buscaDoPeriodo(window.location.search, novo, padraoRef.current);
    if (busca !== window.location.search) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${busca}${window.location.hash}`);
    }
  }, []);
  return [periodo, definir];
}
